/**
 * Largest first, so a value takes the biggest unit it reaches.
 * Billions have no unit: millions run to six digits (`999,999.9M`) before trillions.
 */
const COMPACT_UNITS = [
  { size: 1e12, suffix: 'T' },
  { size: 1e6, suffix: 'M' },
  { size: 1e3, suffix: 'k' },
] as const;

const compactFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Compact number for labels, to one decimal: `1100 → "1.1k"`, `2199000 → "2.2M"`,
 * `3400000000 → "3,400.0M"`, `-20000 → "-20.0k"`, `4000 → "4.0K"` (uppercase).
 * Values within ±1000 are returned as-is.
 */
export function formatCompactNumber(value: number, opts: { uppercase?: boolean } = {}): string {
  const magnitude = Math.abs(value);
  const index = COMPACT_UNITS.findIndex(({ size }) => magnitude >= size);
  if (index === -1) {
    return String(value);
  }
  // A figure that rounds up to the next unit's size reads in that unit: 999,950 is "1.0M", not "1,000.0k".
  const larger = COMPACT_UNITS[index - 1];
  const rounded = Math.round((magnitude / COMPACT_UNITS[index]!.size) * 10) / 10;
  const roundsToNextUnit = larger !== undefined && rounded >= larger.size / COMPACT_UNITS[index]!.size;
  const unit = COMPACT_UNITS[roundsToNextUnit ? index - 1 : index]!;
  const suffix = unit.suffix === 'k' && opts.uppercase ? 'K' : unit.suffix;
  return `${compactFormatter.format(value / unit.size)}${suffix}`;
}

/** Percentage with one decimal place: `72.5 → "72.5%"`. Non-finite input → `''`. */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  return `${value.toFixed(1)}%`;
}

/** `value` as a percentage of `total`: `(1, 4) → "25.0%"`. A non-positive `total` → `''`, never `NaN%`. */
export function formatShare(value: number, total: number): string {
  return total > 0 ? formatPercent((value / total) * 100) : '';
}

/** Millisecond duration with two decimals: `12.3 → "12.30ms"`. Non-finite input → `''`. */
export function formatMilliseconds(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  return `${value.toFixed(2)}ms`;
}

const countFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * Formats an integer quantity with thousands separators (`12,345`).
 * Returns an empty string for null/undefined/non-finite input.
 */
export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '';
  }
  return countFormatter.format(value);
}

/** Average of parts weighed by their value count, sum ÷ average; `null` when a count cannot be recovered. */
export function combineAverages(parts: Iterable<{ sum: number | null; average: number | null }>): number | null {
  let total = 0;
  let valueCount = 0;
  for (const { sum, average } of parts) {
    if (sum === null || (average === null && sum !== 0) || average === 0) {
      return null;
    }
    if (average !== null) {
      total += sum;
      valueCount += Math.round(sum / average);
    }
  }

  return valueCount ? total / valueCount : null;
}
